import React from 'react';
import type { PortalSubscriptionView } from '@/api/portal';
import {
  findStripePrice,
  formatStripePrice,
  type PublicStripePrice,
} from '@/utils/formatStripePrice';
import {
  formatPortalDate,
  usePortalActions,
  usePortalBlockPresence,
} from './portalActionsContext';
import { PortalInvite } from './PortalEntryRequestForm';
import type { TextRoleStyle } from '../semanticTextRoles';
import {
  getPublicQuizStrings,
  type PublicPortalStrings,
} from '@/utils/publicQuizStrings';
import { resolvePortalText } from '@/utils/portalLegacyDefaults';
import { pickStrings } from '@/i18n';
import { editorStrings } from '@/i18n/dictionaries/editor';

/**
 * WEB-1152 (П9) атом 3 — блок `subscription_list` портала отмены.
 *
 * Публичный рендер списка подписок покупателя (GET /public/portal/
 * subscriptions через PublicPortalProvider): цена, статус, дата продления,
 * пометка «отмена запланирована». Вне провайдера (канвас редактора) —
 * инертное превью с примером строки.
 */
export interface SubscriptionListBlockProps {
  title?: string;
  emptyText?: string;
  stripePrices?: PublicStripePrice[];
  /**
   * WEB-1692 юнит 3 (репорт владельца 24.08, три требования сразу). До этой
   * правки сюда приходили ровно три значения и НИ ОДНОГО оформительского —
   * ветка рендера отдала отрисовку компоненту и не передала ему настроек.
   * Накладки спредятся ПОСЛЕ прежних строк, при пустых полях равны пустому
   * объекту: старая воронка не меняется ни на байт.
   */
  titleStyle?: TextRoleStyle;
  /** Содержимое плашки: строка «тариф — статус» и строка продления. */
  rowStyle?: TextRoleStyle;
  /**
   * Надпись пустого состояния. СВОЯ пара, а не часть содержимого плашки: её
   * видит ПОКУПАТЕЛЬ на странице управления подпиской, когда активных подписок
   * нет, — боевой текст, а не служебная заглушка редактора.
   */
  emptyStyle?: TextRoleStyle;
  /** Заливка ВНУТРИ плашки подписки. Пусто = без заливки, как сегодня. */
  cardBackgroundColor?: string;
  /**
   * WEB-1905: язык ВОРОНКИ (`varCtx.system.locale` у рендерера). Надписи и
   * дата продления идут из словаря плеера по нему, а не по языку браузера.
   */
  language?: string | null;
}

/**
 * WEB-1905: слово статуса берётся из словаря плеера по языку воронки.
 * Незнакомый статус (Stripe завёл новый) остаётся техническим словом с
 * заглавной — это честнее выдуманного перевода.
 */
function statusLabel(status: string, portalStrings: PublicPortalStrings): string {
  return (
    portalStrings.statusLabels[status] ??
    (status ? status.charAt(0).toUpperCase() + status.slice(1) : '—')
  );
}

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e5e5',
  borderRadius: 12,
  padding: '12px 16px',
  marginBottom: 8,
  textAlign: 'left',
};

const rowTopStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 12,
  fontWeight: 600,
};

const rowSubStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 13,
  color: '#8c8c8c',
};

function SubscriptionRow({
  sub,
  stripePrices,
  rowStyle,
  cardBackgroundColor,
  language,
}: {
  sub: PortalSubscriptionView;
  stripePrices?: PublicStripePrice[];
  rowStyle?: TextRoleStyle;
  cardBackgroundColor?: string;
  language?: string | null;
}) {
  const t = getPublicQuizStrings(language).portal;
  const price = findStripePrice(stripePrices, sub.priceId ?? undefined);
  const priceLabel = price ? formatStripePrice(price) : '—';
  const periodEnd = formatPortalDate(sub.currentPeriodEnd, language);
  return (
    <div
      // WEB-1692: заливка плашки — ПОСЛЕ прежних строк; пусто = {}.
      style={{ ...cardStyle, ...cardFillStyle(cardBackgroundColor) }}
      data-testid={`portal-subscription-${sub.subscriptionId}`}
    >
      <div style={{ ...rowTopStyle, ...(rowStyle ?? {}) }}>
        <span>{priceLabel}</span>
        <span>{statusLabel(sub.status, t)}</span>
      </div>
      <div style={{ ...rowSubStyle, ...(rowStyle ?? {}) }}>
        {sub.cancelAtPeriodEnd ? (
          <span>
            {periodEnd
              ? t.cancellationScheduledOn(periodEnd)
              : t.cancellationScheduled}
          </span>
        ) : periodEnd ? (
          <span>{t.renewsOn(periodEnd)}</span>
        ) : null}
      </div>
    </div>
  );
}

/** Заливка плашки: пусто = ключа `background` нет вовсе, разметка прежняя. */
function cardFillStyle(color?: string): React.CSSProperties {
  const own = color?.trim();
  return own ? { background: own } : {};
}

export function SubscriptionListBlock({
  title,
  emptyText,
  stripePrices,
  titleStyle,
  rowStyle,
  emptyStyle,
  cardBackgroundColor,
  language,
}: SubscriptionListBlockProps) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  // Регресс-фикс: сообщаем провайдеру, что блок сейчас на экране, — хозяин
  // приглашения выбирается среди ВИДИМЫХ блоков (см. usePortalBlockPresence).
  const isInviteHost = usePortalBlockPresence();

  // WEB-1905 (Ф2 ревизии пачки 16): заголовок, который автор не задал, берётся
  // из словаря плеера по языку ВОРОНКИ. Раньше конструктор писал сюда
  // английское «Your subscriptions» прямо в содержимое блока.
  //
  // WEB-1905, остаток от приёмки 07.09: у пейволлов, заведённых ДО 05.09, это
  // английское умолчание так и лежит в содержимом и перебивало словарь —
  // покупатель русской воронки читал «Your subscriptions» рядом с русской
  // датой. Старое умолчание из реестра считается НЕзаданным (реестр —
  // `utils/portalLegacyDefaults.ts`, авторский текст не трогается).
  const resolvedTitle = resolvePortalText(title, t.listTitle);
  const heading = (
    <p
      data-testid="portal-subscription-list-heading"
      // WEB-1692: накладка роли `Title` — ПОСЛЕ прежних строк; пусто = {}.
      style={{ fontWeight: 700, marginBottom: 12, ...(titleStyle ?? {}) }}
    >
      {resolvedTitle}
    </p>
  );

  if (!portal) {
    // Канвас редактора / страница без портального провайдера — превью.
    return (
      <div
        data-testid="portal-subscription-list-preview"
        style={{ marginBottom: 16 }}
      >
        {heading}
        <div style={{ ...cardStyle, ...cardFillStyle(cardBackgroundColor) }}>
          <div style={{ ...rowTopStyle, ...(rowStyle ?? {}) }}>
            <span>$9.99/mo</span>
            <span>{t.statusLabels.active}</span>
          </div>
          {/* WEB-1692: на канвасе эта строка показывает ТЕКСТ ПУСТОГО
              СОСТОЯНИЯ («здесь появятся подписки»), а не содержимое плашки —
              значит и красится настройкой заглушки, чтобы автор видел ровно
              то, что настраивает. */}
          <div
            data-testid="portal-subscription-list-empty-preview"
            style={{ ...rowSubStyle, ...(emptyStyle ?? {}) }}
          >
            {pickStrings(editorStrings).portalSubscriptionListPreviewHint}
          </div>
        </div>
      </div>
    );
  }

  // WEB-1152 (вход по ?code=): пока код резолвится, входа ещё нет, но
  // loading=true — сперва загрузка, «ссылка недействительна» только когда
  // резолв завершился и вход так и не появился. WEB-1192 (п.3): вместо guid
  // из URL блок проверяет hasAccess — креденшел (сессия) живёт в провайдере.
  if (portal.loading) {
    return <p style={{ marginBottom: 16 }}>{t.loading}</p>;
  }
  if (!portal.hasAccess) {
    // Ревизия WEB-1152: сожжённый одноразовый код получает честное
    // объяснение (не «откройте письмо снова» — это невыполнимо), а ниже —
    // форма «пришлите ссылку по почте» (п.5): дверь в портал без manage_link.
    // Ревизия 2 (дефект 5): текст и форма — одним куском и ровно у одного
    // блока страницы; кого назначили, решает провайдер (inviteHost).
    // WEB-1905 (Ф1 ревизии пачки 16): язык воронки едет и сюда. Три двери из
    // четырёх (кнопки отмены/возобновления, карточка оффера) передавали его, а
    // эта — нет: покупатель русской воронки получал английское объяснение и
    // английскую форму запроса ссылки.
    return <PortalInvite active={isInviteHost} language={language} />;
  }
  if (portal.error) {
    return <p style={{ marginBottom: 16 }}>{t.loadError}</p>;
  }
  if (portal.subscriptions.length === 0) {
    return (
      <p
        data-testid="portal-subscription-list-empty"
        // WEB-1692: боевой текст пустого состояния — своя пара настроек.
        style={{ marginBottom: 16, ...(emptyStyle ?? {}) }}
      >
        {emptyText || t.empty}
      </p>
    );
  }

  return (
    <div data-testid="portal-subscription-list" style={{ marginBottom: 16 }}>
      {heading}
      {portal.subscriptions.map((sub) => (
        <SubscriptionRow
          key={sub.subscriptionId}
          sub={sub}
          stripePrices={stripePrices}
          rowStyle={rowStyle}
          cardBackgroundColor={cardBackgroundColor}
          language={language}
        />
      ))}
    </div>
  );
}
